import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { FileSpreadsheet, Layers, Plus, Printer } from 'lucide-react'
import { toast } from 'sonner'

import type { ListLotsParams } from '@/lib/api/scenting-lot-api'
import { scentingLotApi } from '@/lib/api/scenting-lot-api'
import {
    downloadBlob,
    filenameFromContentDisposition,
} from '@/lib/download-blob'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { remoteSelectFilter } from '@/components/common/filters/remote-select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    LOT_CARD_CODE_CLASS,
    LotListCard,
} from '@/components/common/LotListCard'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { usePrintScentingLotLabel } from '@/hooks/use-lot-label-print'
import {
    useScentingLots,
    useScentingLotsInfinite,
} from '@/hooks/use-scenting-lot-query'
import { useTeaGradesList } from '@/hooks/use-tea-grade-query'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Ô tìm kiếm trước đây chỉ áp dụng khi bấm Enter và dùng `defaultValue` nên
 * không đồng bộ ngược (bấm Back thì ô vẫn giữ chữ cũ). `textFilter` lo cả
 * debounce lẫn đồng bộ ngược.
 */
const FILTERS = defineListFilters({
    q: textFilter({
        label: m.sl_lots_search_label,
        placeholder: m.scl_lots_search_placeholder,
    }),
    grade_id: remoteSelectFilter({
        label: m.sl_lots_filter_grade,
        allLabel: m.sl_lots_filter_all_grades,
        useOptions: () => {
            const { data } = useTeaGradesList(undefined, 'scenting')
            return (data?.data ?? []).map((g) => ({
                value: g.id,
                label: g.name,
            }))
        },
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/scenting-warehouse/lots/',
)({
    component: ScentingLotsPage,
    validateSearch: FILTERS.validateSearch,
})

// Export named để test render được màn thật: autoCodeSplitting tách `component`
// khỏi route nên `Route.options.component` chỉ còn là stub rỗng.
export function ScentingLotsPage() {
    const navigate = useNavigate({ from: '/scenting-warehouse/lots/' })
    const urlSearch = Route.useSearch()

    // Chip lọc theo loại chè cần TÊN, URL chỉ có id → tra runtime rồi truyền
    // xuống; chưa tra được thì không sinh chip.
    const gradeId =
        typeof urlSearch.grade_id === 'string' ? urlSearch.grade_id : ''
    const { data: grades } = useTeaGradesList(undefined, 'scenting')
    const gradeName = (grades?.data ?? []).find((g) => g.id === gradeId)?.name

    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
        chipLabels: {
            grade_id: gradeName
                ? `${m.sl_lots_filter_grade()}: ${gradeName}`
                : undefined,
        },
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const apiFilters = useMemo(
        () => ({
            ...(values.grade_id !== 'all' ? { grade_id: values.grade_id } : {}),
            ...(values.q ? { search: values.q } : {}),
        }),
        [values.grade_id, values.q],
    )

    const params = useMemo<ListLotsParams>(
        () => ({ page, per_page: 50, ...apiFilters }),
        [page, apiFilters],
    )
    const isMobile = useIsMobile()

    const { data, isLoading, isFetching, refetch } = useScentingLots(params, {
        enabled: !isMobile,
    })

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({ per_page: 50, ...apiFilters }),
        [apiFilters],
    )
    const infinite = useScentingLotsInfinite(infiniteParams, {
        enabled: isMobile,
    })
    const mobileLots = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const lots = data?.data ?? []
    const total = data?.meta?.total ?? 0
    const printMut = usePrintScentingLotLabel()

    // Xuất Excel theo filter đang áp (BE tự quét toàn bộ lô còn khả dụng).
    const [exporting, setExporting] = useState(false)
    const handleExport = async () => {
        setExporting(true)
        try {
            const res = await scentingLotApi.exportExcel(params)
            downloadBlob(
                res.data,
                filenameFromContentDisposition(
                    res.headers['content-disposition'] as string | undefined,
                ) ?? 'lo-lay-huong.xlsx',
            )
        } catch {
            toast.error(m.lot_list_export_failed())
        } finally {
            setExporting(false)
        }
    }

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {barProps.activeCount > 0 || values.q
                    ? m.list_empty_filtered()
                    : m.scl_lots_empty()}
            </span>
            {barProps.activeCount > 0 || values.q ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<Layers className="size-5 shrink-0" />}
                title={m.scl_lots_title()}
                utilityActions={
                    <>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleExport()}
                            disabled={exporting}
                            title={m.lot_list_export_btn()}
                            aria-label={m.lot_list_export_btn()}
                        >
                            <FileSpreadsheet className="size-4 md:mr-1" />
                            <span className="hidden md:inline">
                                {exporting
                                    ? m.lot_list_exporting()
                                    : m.lot_list_export_btn()}
                            </span>
                        </Button>
                    </>
                }
                primaryActions={
                    <Button asChild>
                        <Link
                            to="/scenting-warehouse/stock-in-notes/new"
                            className="w-full"
                        >
                            <Plus className="size-4 mr-1" />
                            {m.scl_lots_create_pnk()}
                        </Link>
                    </Button>
                }
            />

            <ListFilterBar
                {...barProps}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.sl_lots_loading()}
                    </div>
                ) : mobileLots.length === 0 ? (
                    emptyState
                ) : (
                    <>
                        {mobileLots.map((lot) => (
                            <LotListCard
                                key={lot.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/scenting-warehouse/lots/$id',
                                        params: { id: lot.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/scenting-warehouse/lots/$id"
                                        params={{ id: lot.id }}
                                        className={LOT_CARD_CODE_CLASS}
                                    >
                                        {lot.code}
                                    </Link>
                                }
                                attribute={lot.grade_name ?? '—'}
                                quantityAvailable={lot.quantity_available}
                                quantityUnit="kg"
                                createdAt={lot.created_at}
                                actions={
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        aria-label={m.lot_label_print_btn()}
                                        title={m.lot_label_print_btn()}
                                        disabled={printMut.isPending}
                                        onClick={() => printMut.mutate(lot.id)}
                                    >
                                        <Printer className="size-4" />
                                    </Button>
                                }
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_lots_col_code()}</TableHead>
                            <TableHead>{m.sl_lots_col_grade()}</TableHead>
                            <TableHead className="text-right">
                                {m.sl_lots_col_available()}
                            </TableHead>
                            <TableHead>{m.sl_lots_col_created_at()}</TableHead>
                            <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={5}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.sl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : lots.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            lots.map((lot) => (
                                <TableRow
                                    key={lot.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/scenting-warehouse/lots/$id',
                                            params: { id: lot.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/scenting-warehouse/lots/$id"
                                            params={{ id: lot.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {lot.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{lot.grade_name}</TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(lot.quantity_available)}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(lot.created_at)}
                                    </TableCell>
                                    <TableCell
                                        className="text-right"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            title={m.lot_label_print_btn()}
                                            disabled={printMut.isPending}
                                            onClick={() =>
                                                printMut.mutate(lot.id)
                                            }
                                        >
                                            <Printer className="size-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — desktop/tablet only */}
            <div className="hidden md:flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / 50)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}
